import * as React from 'react';

import { wait } from 'common/helpers/utils';

import type { DialogProps } from '../Dialog/Dialog';
import type { DialogComponent, DialogContextState, DialogContextValues } from './context';
import { DialogContext } from './context';

export type DialogComponentProps<T extends DialogProps> = Omit<T, 'onClose'> & Partial<{ onClose: CloseDialogFn }>;

export type OpenDialogFn = <T extends DialogProps>(
  component: DialogComponent<T>,
  props: DialogComponentProps<T>
) => void;

export type CloseDialogFn = (payload?: unknown) => void | unknown | Promise<void>;

class DialogProvider extends React.Component<React.PropsWithChildren<Record<string, unknown>>, DialogContextState> {
  state: DialogContextState = {
    component: null,
    props: undefined,
    isOpen: false,
  };

  openDialog: OpenDialogFn = <T extends DialogProps>(component: DialogComponent<T>, props: DialogComponentProps<T>) => {
    this.setState({
      component,
      props: props as T,
      isOpen: true,
    });
  };

  closeDialog: CloseDialogFn = async (payload?: unknown) => {
    const { onClose } = this.state.props ?? {};

    if (onClose) {
      onClose(payload);
    }

    this.setState({
      isOpen: false,
    });

    await wait(200);

    this.setState({
      component: null,
      props: undefined,
    });
  };

  render() {
    const value: DialogContextValues = {
      ...this.state,
      openDialog: this.openDialog,
      closeDialog: this.closeDialog,
    };

    return <DialogContext.Provider value={value}>{this.props.children}</DialogContext.Provider>;
  }
}

export default DialogProvider;
